import { useState } from 'react';
import { X, Pencil, Trash2, Check, Ruler, HardDrive, FileImage, Calendar, User } from 'lucide-react';
import { IconButton } from '../../Button';
import { Button } from '../../Button';
import type { AssetPreviewData, ValidationResult } from '../types';

interface AssetPreviewOverlayProps {
  asset: AssetPreviewData;
  validationResults?: ValidationResult[];
  readOnly?: boolean;
  onClose: () => void;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusIcon = (status: string) => {
  if (status === 'pass') return <span className="text-green-500">&#10003;</span>;
  if (status === 'warning') return <span className="text-amber-500">&#9888;</span>;
  return <span className="text-red-500">&#10005;</span>; // fail or error
};

export const AssetPreviewOverlay = ({ asset, validationResults = [], readOnly = false, onClose, onRename, onDelete }: AssetPreviewOverlayProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(asset.name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== asset.name) onRename(asset.id, trimmed);
    setIsEditing(false);
  };

  const ext = asset.originalName.split('.').pop()?.toUpperCase() ?? '';
  const uploaded = new Date(asset.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const isTransparent = asset.hasTransparency === true || asset.mimeType === 'image/png' || asset.mimeType === 'image/svg+xml';
  const uploaderLabel =
    asset.uploadedBy?.name ??
    (asset.source === 'printtat' ? 'Printtat' : asset.source === 'user' ? 'You' : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <IconButton size="sm" onClick={onClose} className="absolute top-3 right-3 z-10 bg-black/5 hover:bg-black/10">
          <X className="w-4 h-4 text-gray-500" />
        </IconButton>

        {/* Image preview */}
        <div
          className="flex items-center justify-center p-6"
          style={isTransparent ? {
            backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
          } : { backgroundColor: '#fafafa' }}
        >
          <img
            src={asset.url}
            alt={asset.name}
            crossOrigin="anonymous"
            className="max-h-[50vh] max-w-full object-contain rounded"
          />
        </div>

        {/* Info footer */}
        <div className="px-5 pb-5 pt-4 space-y-3">
          {/* Name + actions row */}
          {readOnly ? (
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">{asset.name}</h2>
            </div>
          ) : confirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Delete this image?</span>
              <div className="ml-auto flex items-center gap-1.5">
                <Button
                  size="sm"
                  color="red"
                  onClick={() => { setConfirmingDelete(false); onDelete(asset.id); onClose(); }}
                >
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray"
                  onClick={() => setConfirmingDelete(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : isEditing ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setIsEditing(false); }}
                className="flex-1 text-sm font-medium px-2 py-1 border border-blue-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
              />
              <IconButton size="sm" onClick={handleSaveName} className="text-blue-500 hover:text-blue-700 hover:bg-blue-50">
                <Check className="w-4 h-4" />
              </IconButton>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">{asset.name}</h2>
              <IconButton size="xs" onClick={() => { setEditName(asset.name); setIsEditing(true); }}>
                <Pencil className="w-3 h-3" />
              </IconButton>
              <IconButton
                size="xs"
                onClick={() => setConfirmingDelete(true)}
                className="ml-auto hover:text-red-500 hover:bg-red-50"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </IconButton>
            </div>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-gray-400">
            <span className="flex items-center gap-1">
              <Ruler className="w-3 h-3" />
              {asset.width} &times; {asset.height} px
            </span>
            <span className="flex items-center gap-1">
              <HardDrive className="w-3 h-3" />
              {formatBytes(asset.size)}
            </span>
            <span className="flex items-center gap-1">
              <FileImage className="w-3 h-3" />
              {ext}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {uploaded}
            </span>
            {uploaderLabel && (
              <span
                className="flex items-center gap-1"
                title={asset.uploadedBy?.email}
              >
                <User className="w-3 h-3" />
                {uploaderLabel}
              </span>
            )}
          </div>

          {/* Validation checks */}
          {validationResults.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Print checks</p>
              {validationResults
                .filter(r => r.message)
                .map(r => (
                <div
                  key={r.key}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
                    r.status === 'pass' ? 'bg-emerald-50' :
                    r.status === 'warning' ? 'bg-amber-50' :
                    'bg-red-50'
                  }`}
                >
                  <span className="mt-0.5 flex-shrink-0">{statusIcon(r.status)}</span>
                  <div className="min-w-0">
                    <p className={`font-medium ${
                      r.status === 'pass' ? 'text-emerald-800' :
                      r.status === 'warning' ? 'text-amber-800' :
                      'text-red-800'
                    }`}>{r.title}</p>
                    <p className={`mt-0.5 ${
                      r.status === 'pass' ? 'text-emerald-600' :
                      r.status === 'warning' ? 'text-amber-600' :
                      'text-red-600'
                    }`}>{r.message}</p>
                    {r.suggestion && (
                      <p className={`mt-0.5 italic ${
                        r.status === 'warning' ? 'text-amber-500' : 'text-red-500'
                      }`}>{r.suggestion}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
